import { cn } from './utils';

/**
 * Color-coded badge for the offer.status enum — the full 10-state set
 * (pending, countered, accepted, deposit_held, completed, rejected,
 * withdrawn, refused, cancelled, expired).
 *
 * SIL-322: promoted from customer offers.tsx, which was the SUPERSET of the
 * two customer StatusBadge copies — the listing-detail embedded badge
 * (listings_.$listingId.tsx) only handled 4 of the 8 states (pending,
 * accepted, rejected, withdrawn) and silently fell back to the raw status
 * token + a generic zinc style for countered/deposit_held/completed/refused,
 * and never rendered the refusal reason. Using this shared superset badge
 * there fixes that latent bug.
 */

// RUF-570: seller sees "awaiting deposit" (accepted) / "deposit held in
// escrow" (deposit_held) instead of the raw status token.
// RUF-572: both parties see "Refused by SiliconX" instead of the raw
// 'refused' token, plus the refusal reason as italic subtext.
// SIL-645: 'cancelled' shows the recorded cancellationReason the same way.
const LABELS: Record<string, string> = {
  accepted: 'awaiting deposit',
  deposit_held: 'deposit held in escrow',
  refused: 'Refused by SiliconX',
  cancelled: 'Cancelled',
  // SIL-1427/migration 972: a buy_now offer the seller did not act on within
  // its 48h SLA — distinct from withdrawn/rejected (see offer.status comment).
  expired: 'Expired',
};

const STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  countered: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  deposit_held: 'bg-teal-100 text-teal-700',
  completed: 'bg-muted text-foreground',
  rejected: 'bg-red-100 text-red-700',
  withdrawn: 'bg-muted text-foreground',
  refused: 'bg-red-200 text-red-900',
  cancelled: 'bg-zinc-200 text-zinc-800',
  expired: 'bg-zinc-200 text-zinc-800',
};

/** Pure label function — used directly by the badge and unit-tested. */
export function offerStatusLabel(status: string): string {
  return LABELS[status] ?? status;
}

export function OfferStatusBadge({
  value,
  refusalReason,
  cancellationReason,
}: {
  value: string;
  refusalReason?: string | null;
  cancellationReason?: string | null;
}) {
  const style = STYLES[value] ?? 'bg-muted text-foreground';
  const reason =
    value === 'refused' ? refusalReason : value === 'cancelled' ? cancellationReason : null;
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={cn(
          'inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium',
          style,
        )}
      >
        {offerStatusLabel(value)}
      </span>
      {reason && (
        <span className="max-w-[16rem] text-xs italic text-muted-foreground">
          &ldquo;{reason}&rdquo;
        </span>
      )}
    </div>
  );
}
